import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { LAMBDA_BASE_URL } from 'constants';
import { useAuth } from './AuthContext';
import encryptedStorage from '../utils/encryptedStorage';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const { currentDomain } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [fetchAttempts, setFetchAttempts] = useState(0);
  
  const [currentDomainId, setCurrentDomainId] = useState(null);

  const getStorageKey = useCallback(() => {
    return `settings_${currentDomain?.domain_token || 'default'}`;
  }, [currentDomain?.domain_token]);

  useEffect(() => {
    if (currentDomain?.domain_token !== currentDomainId) {
      setSettings(null);
      setCurrentDomainId(currentDomain?.domain_token);
      setFetchAttempts(0);
      const storageKey = getStorageKey();
      encryptedStorage.removeItem(storageKey);
    }
  }, [currentDomain, currentDomainId, getStorageKey]);

  const fetchSettings = useCallback(async (forceRefresh = false) => {
    if (!currentDomain?.domain_token) {
      setLoading(false);
      return null;
    }

    const storageKey = getStorageKey();
    const now = Date.now();
    const cacheDuration = 5 * 60 * 1000;

    try {
      setLoading(true);
      setError(null);

      if (!forceRefresh && storageKey) {
        const cached = encryptedStorage.getItem(storageKey);
        
        if (cached && 
            cached.timestamp && 
            (now - cached.timestamp < cacheDuration) &&
            cached.domainId === currentDomain.domain_token) {
          
          setSettings(cached.data);
          setLastFetched(cached.timestamp);
          setLoading(false);
          return cached.data;
        }
      }

      const domainId = currentDomain.domain_token || encryptedStorage.getItem('domain_token');
      const apiUrl = LAMBDA_BASE_URL;
      
      const res = await axios.get(`${apiUrl}/settings`, {
        headers: {
          'Content-Type': 'application/json',
          'DOMAIN-TOKEN': domainId,
        },
      });

      if (res.status === 200) {
        const data = res.data;
        const result = data?.result;
        
        // console.log('Settings API response:', {
        //   statusCode: data?.statusCode,
        //   resultCode: data?.resultCode,
        //   result: result,
        //   hasValidResult: result && typeof result === 'object' && !Array.isArray(result)
        // });

        // Check if result is 404 (number) or an object with 404
        const isNotFound = result === 404 || (result && result === 404);
        
        if (isNotFound) {
          // console.log('Settings API returned 404 in result, settings not found yet');
          const emptySettings = formatSettings({});
          setSettings(emptySettings);
          setLastFetched(now);
          
          if (storageKey) {
            encryptedStorage.setItem(storageKey, {
              data: emptySettings,
              timestamp: now,
              domainId: currentDomain.domain_token,
            });
          }
          
          setLoading(false);
          setFetchAttempts(prev => prev + 1);
          throw new Error('SETTINGS_NOT_FOUND_404');
        }

        // Handle case where result is not an object
        if (!result || typeof result !== 'object' || Array.isArray(result)) {
          // console.log('Settings API returned invalid result format:', result);
          const emptySettings = formatSettings({});
          setSettings(emptySettings);
          setLastFetched(now);
          
          if (storageKey) {
            encryptedStorage.setItem(storageKey, {
              data: emptySettings,
              timestamp: now,
              domainId: currentDomain.domain_token,
            });
          }
          
          setLoading(false);
          setFetchAttempts(prev => prev + 1);
          throw new Error('INVALID_RESULT_FORMAT');
        }

        // Check for ansera_search_chat_curl array
        const chatCurlArray = result.ansera_search_chat_curl;
        if (!Array.isArray(chatCurlArray) || chatCurlArray.length === 0) {
          // console.log('Settings API returned empty chatCurlArray');
          const emptySettings = formatSettings({});
          setSettings(emptySettings);
          setLastFetched(now);
          
          if (storageKey) {
            encryptedStorage.setItem(storageKey, {
              data: emptySettings,
              timestamp: now,
              domainId: currentDomain.domain_token,
            });
          }
          
          setLoading(false);
          setFetchAttempts(prev => prev + 1);
          throw new Error('EMPTY_CHAT_CURL_ARRAY');
        }

        const settingData = chatCurlArray[0] || {};
        const formattedSettings = formatSettings(settingData);
        
        // console.log('Successfully loaded settings:', {
        //   appearance: formattedSettings.ansera_search_option3_widget,
        //   theme: formattedSettings.ansera_search_theme,
        //   logoUrl: formattedSettings.ansera_search_logo_url,
        //   navIconScriptName: formattedSettings.ansera_search_nav_icon_script_name,
        //   hasRawData: Object.keys(formattedSettings._raw || {}).length > 0
        // });
        
        setSettings(formattedSettings);
        setLastFetched(now);
        setFetchAttempts(0); // Reset attempts on success
        
        if (storageKey) {
          encryptedStorage.setItem(storageKey, {
            data: formattedSettings,
            timestamp: now,
            domainId: currentDomain.domain_token,
          });
        }

        setLoading(false);
        return formattedSettings;
      } else {
        // console.log(`Settings API returned HTTP status ${res.status}`);
        const emptySettings = formatSettings({});
        setSettings(emptySettings);
        setLastFetched(now);
        setFetchAttempts(prev => prev + 1);
        
        if (storageKey) {
          encryptedStorage.setItem(storageKey, {
            data: emptySettings,
            timestamp: now,
            domainId: currentDomain.domain_token,
          });
        }
        
        setLoading(false);
        throw new Error(`HTTP_STATUS_${res.status}`);
      }
    } catch (err) {
      console.error('Error fetching settings:', err.message);
      
      // Only set error if it's not a 404/not found error
      if (!err.message.includes('404') && !err.message.includes('SETTINGS_NOT_FOUND')) {
        setError(err.message || 'Failed to fetch settings');
      }
      
      // Try to use cached data if available
      const storageKey = getStorageKey();
      if (storageKey) {
        const cached = encryptedStorage.getItem(storageKey);
        if (cached && cached.domainId === currentDomain?.domain_token) {
          // console.log('Using cached settings');
          // setSettings(cached.data);
          // setLastFetched(cached.timestamp);
          // setLoading(false);
          return cached.data;
        }
      }
      
      // If no cache, return empty settings but don't set error for 404
      const emptySettings = formatSettings({});
      setSettings(emptySettings);
      setLastFetched(Date.now());
      setLoading(false);
      
      return emptySettings;
    }
  }, [currentDomain, getStorageKey]);

  const updateSettings = useCallback(async (updates) => {
    if (!currentDomain?.domain_token) {
      throw new Error('No domain token available');
    }

    try {
      const domainId = currentDomain.domain_token;
      const apiUrl = LAMBDA_BASE_URL;
      
      const payload = {};
      Object.keys(updates).forEach(key => {
        payload[key] = updates[key];
      });

      // console.log('Updating settings with payload:', payload);

      const response = await axios.patch(`${apiUrl}/settings`, payload, {
        headers: {
          'Content-Type': 'application/json',
          'DOMAIN-TOKEN': domainId,
        },
      });

      if (response.data?.resultCode === "0") {
        const updatedSettings = settings ? { ...settings, ...payload } : formatSettings(payload);
        setSettings(updatedSettings);
        setFetchAttempts(0); // Reset attempts on successful update

        const storageKey = getStorageKey();
        if (storageKey) {
          encryptedStorage.setItem(storageKey, {
            data: updatedSettings,
            timestamp: Date.now(),
            domainId: currentDomain.domain_token,
          });
        }

        setLastFetched(Date.now());
        
        // console.log('Settings updated successfully');
        return updatedSettings;
      } else {
        throw new Error(`Failed to update settings: ${response.data?.messages?.[0] || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error updating settings:', err);
      setError(err.message || 'Failed to update settings');
      throw err;
    }
  }, [currentDomain, settings, getStorageKey]);

  const formatSettings = (rawSettings) => {
    const hasData = rawSettings && Object.keys(rawSettings).length > 0;
    
    const settingsObj = {
      ansera_search_display_default_questions: rawSettings.ansera_search_display_default_questions || 0,
      ansera_search_default_questions: rawSettings.ansera_search_default_questions 
        ? (typeof rawSettings.ansera_search_default_questions === 'string' 
           ? JSON.parse(rawSettings.ansera_search_default_questions)
           : rawSettings.ansera_search_default_questions)
        : null,
      
      ansera_search_google_recaptcha_v3_enable: rawSettings.ansera_search_google_recaptcha_v3_enable || 0,
      ansera_search_google_recaptcha_v3_key_id: rawSettings.ansera_search_google_recaptcha_v3_key_id || "",
      ansera_search_google_recaptcha_v3_key_secret: rawSettings.ansera_search_google_recaptcha_v3_key_secret || "",
      ansera_search_suspicious_message: rawSettings.ansera_search_suspicious_message || "",
      
      ansera_search_email_placeholder: rawSettings.ansera_search_email_placeholder || "",
      email_header: rawSettings.email_header || "",
      email_footer: rawSettings.email_footer || "",
      ansera_search_option3_widget: rawSettings.ansera_search_option3_widget || "search-bar",
      ansera_search_theme: rawSettings.ansera_search_theme || "light",
      ansera_search_logo_url: rawSettings.ansera_search_logo_url || "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-Teal.PNG",
      ansera_search_logo_height: rawSettings.ansera_search_logo_height || "",
      ansera_search_logo_width: rawSettings.ansera_search_logo_width || "",
      ansera_search_search_bar_text: rawSettings.ansera_search_search_bar_text || "",
      no_answer_text: rawSettings.no_answer_text || "",
      ansera_search_contact_us_text: rawSettings.ansera_search_contact_us_text || "",
      ansera_search_contact_us_url: rawSettings.ansera_search_contact_us_url || "",
      ansera_search_feedback_message: rawSettings.ansera_search_feedback_message || "",
      ansera_search_send_mail_text: rawSettings.ansera_search_send_mail_text || "",
      ansera_chat_header_text: rawSettings.ansera_chat_header_text || "",
      
      ansera_search_custom_bg_color: rawSettings.ansera_search_custom_bg_color || "",
      ansera_search_custom_text_color: rawSettings.ansera_search_custom_text_color || "",
      ansera_search_custom_button_bg_color: rawSettings.ansera_search_custom_button_bg_color || "",
      ansera_search_custom_button_hover_color: rawSettings.ansera_search_custom_button_hover_color || "",
      ansera_search_custom_input_border_color: rawSettings.ansera_search_custom_input_border_color || "",
      ansera_search_answer_bg_color: rawSettings.ansera_search_answer_bg_color || "",
      ansera_search_tiles_bg_color: rawSettings.ansera_search_tiles_bg_color || "",
      ansera_search_tiles_text_color: rawSettings.ansera_search_tiles_text_color || "",
      
      ansera_chat_bubble_background_color: rawSettings.ansera_chat_bubble_background_color || "#219197",
      ansera_chat_pane_background_color: rawSettings.ansera_chat_pane_background_color || "#f9f9f9",
      ansera_chat_pane_question_background_color: rawSettings.ansera_chat_pane_question_background_color || "#ffffff",
      ansera_chat_pane_question_text_color: rawSettings.ansera_chat_pane_question_text_color || "#010103ff",
      ansera_search_chat_header_bg_color: rawSettings.ansera_search_chat_header_bg_color || "#ffffff",
      ansera_search_chat_header_text_color: rawSettings.ansera_search_chat_header_text_color || "#000000",
      ansera_search_chat_footer_bg_color: rawSettings.ansera_search_chat_footer_bg_color || "#f1f1f1",
      ansera_search_chat_footer_text_color: rawSettings.ansera_search_chat_footer_text_color || "#000000",
      ansera_search_nav_icon_color: rawSettings.ansera_search_nav_icon_color || "Teal",
      ansera_search_nav_icon_script_name: rawSettings.ansera_search_nav_icon_script_name || "",
      hubspot_sync_interval: rawSettings.hubspot_sync_interval,
      search_bubble_bg_color: rawSettings.search_bubble_bg_color || "#219197",
      ansera_search_bubble_icon_url : rawSettings.ansera_search_bubble_icon_url || "",
      is_whitelabelled: rawSettings.is_whitelabelled || 0,
      whitelabel_scope: rawSettings.whitelabel_scope || null,
      company_logo: rawSettings.company_logo || "",
      search_bar_bubble_logo: rawSettings.search_bar_bubble_logo || "",
      branding_option: rawSettings.branding_option || null,
      whitelabel_company_name: rawSettings.whitelabel_company_name || "",
      whitelabel_company_portal_url: rawSettings.whitelabel_company_portal_url || "",
      url: rawSettings.url || "",
      chat_widget_header_text: rawSettings.chat_widget_header_text || "",
      search_bar_close_button_color: rawSettings.search_bar_close_button_color || "",
      enable_popup_question: rawSettings.enable_popup_question || 0,
      popup_question_load_timer: rawSettings.popup_question_load_timer || 0,
      popup_question_close_timer : rawSettings.popup_question_close_timer || 0,
      autosync_hubspot_new_content: rawSettings.autosync_hubspot_new_content || false,
      ansera_search_icon_spacing: rawSettings.ansera_search_icon_spacing || 0,
      ansera_search_icon_position:rawSettings.ansera_search_icon_position || "",
      ansera_search_bubble_position: rawSettings.ansera_search_bubble_position || "right",
      ansera_chat_widget_height: rawSettings.ansera_chat_widget_height || 0.75,
      ansera_search_input_position: rawSettings.ansera_search_input_position || "bottom",
      ansera_search_company_name: rawSettings.ansera_search_company_name || "",
      company_logo: rawSettings.company_logo || "",
      _raw: rawSettings,
      _hasData: hasData,

    };

    return settingsObj;
  };

  const refreshSettings = useCallback(() => {
    return fetchSettings(true);
  }, [fetchSettings]);

  const clearCache = useCallback(() => {
    const storageKey = getStorageKey();
    if (storageKey) {
      encryptedStorage.removeItem(storageKey);
    }
    setSettings(null);
    setLastFetched(null);
    setFetchAttempts(0);
  }, [getStorageKey]);

  useEffect(() => {
    if (currentDomain) {
      // console.log('Fetching settings for domain:', currentDomain.domain_token);
      fetchSettings();
    } else {
      // console.log('No current domain, clearing settings');
      setSettings(null);
      setLoading(false);
    }
  }, [currentDomain, fetchSettings]);

  const value = {
    settings,
    loading,
    error,
    lastFetched,
    fetchAttempts,
    currentDomainId,
    
    fetchSettings,
    updateSettings,
    refreshSettings,
    clearCache,
    getSetting: (key) => settings?.[key],
    
    isReady: !!settings && !loading,
    hasSettings: !!settings,
    hasRealSettings: settings && settings._hasData === true,
    isEmptySettings: !settings || settings._hasData === false,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsContext;